const _jsxFileName = '';
import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Eye,
  EyeOff,
  ArrowRight,
  Check,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

const backgroundSlides = [
  'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=2070&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=2070&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1602173574767-37ac01994b2a?q=80&w=2070&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?q=80&w=2079&auto=format&fit=crop',
];

const Auth = ({
  onNavigate,
  onLogin,
  initialAdmin = false,
  initialMode = 'login', // ADDED: Default to login
}) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login'); // FIXED: Use initialMode
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(initialAdmin);
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Secret Click Logic
  const clickTimeoutRef = useRef(null);
  const clickCountRef = useRef(0);

  // ADDED: Check URL parameters for mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, []);

  useEffect(() => {
    setIsAdmin(initialAdmin);
  }, [initialAdmin]);

  // Slideshow Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsSuccess(false);
    setError('');
    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
    // Update URL without reload
    const newUrl = isLogin ? '/register' : '/auth';
    window.history.pushState(null, '', newUrl);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSecretClick = (e) => {
    e.preventDefault();

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickCountRef.current += 1;

    if (clickCountRef.current >= 5) {
      onNavigate('admin-portal');
      clickCountRef.current = 0;
    } else {
      clickTimeoutRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 500);
    }
  };

  const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

  // Handle Forgot Password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setResetSuccess(true);
        setResetMessage('Password reset link sent to your email!');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetSuccess(false);
          setResetEmail('');
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage(data.error || data.message || 'Email not found');
      }
    } catch (error) {
      setResetMessage('Connection error. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  // Handle Registration
  const handleRegister = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.name,
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        // Auto-login after successful registration
        const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password }),
        });
        const loginData = await loginResponse.json();
        if (loginData.success) {
          const userData = {
            id: loginData.user.id,
            email: loginData.user.email,
            name: loginData.user.name || loginData.user.username || 'Member',
            role: loginData.user.role || 'member',
            permissions: loginData.user.permissions || [],
            avatar: loginData.user.avatar || '',
            phone: loginData.user.phone,
            created_at: loginData.user.created_at,
          };
          localStorage.setItem('sparklingUser', JSON.stringify(userData));
          if (loginData.token) localStorage.setItem('sparklingToken', loginData.token);
          setTimeout(() => {
            onLogin(userData);
          }, 1000);
        } else {
          // Login failed — fall back to showing login form
          setTimeout(() => {
            setIsLogin(true);
            setIsSuccess(false);
            setFormData({ name: '', email: '', password: '', confirmPassword: '' });
          }, 1500);
        }
      } else {
        setError(data.error || data.message || 'Registration failed');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    }
  };

  // Handle Login
  const handleLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);

        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || data.user.username || 'Member',
          role: data.user.role || 'member',
          permissions: data.user.permissions || [],
          avatar: data.user.avatar || '',
          phone: data.user.phone,
          created_at: data.user.created_at,
        };

        localStorage.setItem('sparklingUser', JSON.stringify(userData));
        if (data.token) localStorage.setItem('sparklingToken', data.token);

        setTimeout(() => {
          onLogin(userData);
        }, 1500);
      } else {
        setError(data.error || data.message || 'Invalid email or password');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    }
  };

  // Handle Admin Login
  const handleAdminLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (data.success && data.user.role && data.user.role !== 'member') {
        setIsSuccess(true);

        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || data.user.username || 'Staff',
          role: data.user.role,
          permissions: data.user.permissions || [],
          avatar: data.user.avatar || '',
          phone: data.user.phone,
          created_at: data.user.created_at,
        };

        localStorage.setItem('sparklingUser', JSON.stringify(userData));
        if (data.token) localStorage.setItem('sparklingToken', data.token);

        setTimeout(() => {
          onLogin(userData);
        }, 1500);
      } else if (data.success && data.user.role === 'member') {
        setError('Access denied. Staff privileges required.');
      } else {
        setError(data.error || data.message || 'Invalid credentials');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (!isLogin && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    // Admin Authentication
    if (isAdmin) {
      await handleAdminLogin();
      setIsLoading(false);
      return;
    }

    // Standard User
    if (isLogin) {
      await handleLogin();
    } else {
      await handleRegister();
    }

    setIsLoading(false);
  };

  return React.createElement(
    'div',
    {
      className: 'min-h-screen flex bg-stone-50 text-stone-900',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 299 },
    },

    /* Left Panel - Image Slideshow */
    React.createElement(
      'div',
      {
        className: 'hidden lg:block lg:w-1/2 relative overflow-hidden bg-stone-900',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 302 },
      },
      backgroundSlides.map((slide, index) =>
        React.createElement('img', {
          key: index,
          src: slide,
          alt: 'Luxury Jewelry',
          className: `absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          } animate-[zoomSlow_25s_infinite_alternate]`,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 304 },
        })
      ),

      React.createElement('div', {
        className: 'absolute inset-0 bg-stone-900/30 z-10',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 314 },
      }),
      React.createElement(
        'div',
        {
          className:
            'absolute inset-0 bg-gradient-to-t from-stone-900/90 via-transparent to-transparent z-20 flex flex-col justify-end p-16 text-white',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 315 },
        },
        React.createElement(
          'h2',
          {
            className:
              'font-serif text-4xl mb-4 leading-tight drop-shadow-lg transition-all duration-500',
            key: isLogin ? 'login' : 'register',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 316 },
          },
          isAdmin
            ? 'Maison Management'
            : isLogin
              ? 'Welcome Back to the Vault'
              : 'Join the Circle of Brilliance'
        ),
        React.createElement(
          'p',
          {
            className:
              'font-light text-stone-200 max-w-md text-sm leading-relaxed border-l border-gold-500 pl-6 drop-shadow-md',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 322 },
          },
          isAdmin
            ? 'Secure access for inventory management, client concierge services, and appointment scheduling.'
            : isLogin
              ? 'Access your curated wishlist, order history, and exclusive member-only viewings.'
              : 'Create an account to unlock bespoke services, early access to new collections, and personalized consultations.'
        ),

        /* Slide Indicators */
        React.createElement(
          'div',
          {
            className: 'flex gap-2 mt-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 333 },
          },
          backgroundSlides.map((_, idx) =>
            React.createElement('div', {
              key: idx,
              className: `h-1 rounded-full transition-all duration-500 ${
                idx === currentSlide ? 'w-8 bg-gold-500' : 'w-2 bg-white/30'
              }`,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 335 },
            })
          )
        )
      )
    ),

    /* Right Panel - Form */
    React.createElement(
      'div',
      {
        className:
          'w-full lg:w-1/2 flex flex-col justify-center items-center p-6 md:p-12 relative bg-white',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 347 },
      },

      /* Back Button */
      React.createElement(
        'button',
        {
          onClick: () => onNavigate('home'),
          className:
            'absolute top-8 left-8 flex items-center gap-2 text-stone-400 hover:text-stone-900 transition-colors text-xs uppercase tracking-widest',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 350 },
        },

        React.createElement(ArrowLeft, {
          size: 14,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 354 },
        }),
        ' Back to Maison'
      ),

      React.createElement(
        'div',
        {
          className: 'w-full max-w-md',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 357 },
        },
        /* Header */
        React.createElement(
          'div',
          {
            className: 'text-center mb-10',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 359 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-center mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 360 },
            },
            isAdmin &&
              React.createElement(
                'div',
                {
                  className: 'bg-stone-100 p-3 rounded-full mb-2 animate-fade-in',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 362 },
                },
                React.createElement(ShieldCheck, {
                  size: 24,
                  className: 'text-stone-700',
                  strokeWidth: 1.5,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 363 },
                })
              )
          ),
          React.createElement(
            'span',
            {
              className: `text-[10px] tracking-[0.3em] uppercase font-bold mb-4 block ${isAdmin ? 'text-stone-500' : 'text-gold-600'}`,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 367 },
            },
            isAdmin ? 'Staff Portal' : isLogin ? 'Member Access' : 'Registration'
          ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-4xl text-stone-900 mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 370 },
            },
            isAdmin ? 'Internal Access' : 'Sparkling Gems'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-400 text-sm font-light italic cursor-default select-none',
              onClick: handleSecretClick,
              title: !isAdmin ? 'Est. 1985' : undefined,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 373 },
            },

            isAdmin ? 'Authorized Personnel Only' : 'Est. 1985'
          )
        ),

        isSuccess
          ? React.createElement(
              'div',
              {
                className: 'bg-stone-50 border border-stone-100 p-8 text-center animate-fade-in',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 383 },
              },
              React.createElement(
                'div',
                {
                  className:
                    'w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 384 },
                },
                React.createElement(Check, {
                  size: 32,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 385 },
                })
              ),
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-2xl mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 387 },
                },
                isAdmin ? 'Access Granted' : isLogin ? 'Welcome Back' : 'Account Created'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-stone-500 text-sm mb-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 390 },
                },
                isAdmin
                  ? 'Redirecting to management dashboard...'
                  : isLogin
                    ? 'You have successfully logged in.'
                    : 'Your account has been created. Please log in.'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 396 },
                },
                'Redirecting...'
              )
            )
          : React.createElement(
              'form',
              {
                onSubmit: handleSubmit,
                className: 'space-y-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 399 },
              },
              /* Error Message */
              error &&
                React.createElement(
                  'div',
                  {
                    className:
                      'bg-red-50 border border-red-100 p-3 rounded flex items-center gap-3 text-red-600 animate-fade-in',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 402 },
                  },
                  React.createElement(AlertCircle, {
                    size: 16,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 403 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-xs',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 404 },
                    },
                    error
                  )
                ),

              /* Name field only for non-admin registration */
              !isLogin &&
                !isAdmin &&
                React.createElement(
                  'div',
                  {
                    className: 'group relative',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 410 },
                  },
                  React.createElement('input', {
                    type: 'text',
                    name: 'name',
                    value: formData.name,
                    onChange: handleChange,
                    required: true,
                    className:
                      'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent',
                    placeholder: 'Full Name',
                    id: 'auth-name',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 411 },
                  }),
                  React.createElement(
                    'label',
                    {
                      htmlFor: 'auth-name',
                      className:
                        'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 421 },
                    },
                    'Full Name'
                  )
                ),

              React.createElement(
                'div',
                {
                  className: 'group relative',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 427 },
                },
                React.createElement('input', {
                  type: isAdmin ? 'text' : 'email',
                  name: 'email',
                  value: formData.email,
                  onChange: handleChange,
                  required: true,
                  className: `w-full bg-transparent border-b py-3 text-stone-900 focus:outline-none transition-colors peer placeholder-transparent ${isAdmin ? 'border-stone-300 focus:border-stone-800' : 'border-stone-300 focus:border-gold-600'}`,
                  placeholder: isAdmin ? 'Username' : 'Email Address',
                  id: 'auth-email',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 428 },
                }),
                React.createElement(
                  'label',
                  {
                    htmlFor: 'auth-email',
                    className: `absolute left-0 -top-3.5 text-xs transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-xs uppercase tracking-wider ${isAdmin ? 'text-stone-800 peer-focus:text-stone-800' : 'text-gold-600 peer-focus:text-gold-600'}`,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 438 },
                  },
                  isAdmin ? 'Username' : 'Email Address'
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'group relative',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 443 },
                },
                React.createElement('input', {
                  type: showPassword ? 'text' : 'password',
                  name: 'password',
                  value: formData.password,
                  onChange: handleChange,
                  required: true,
                  className: `w-full bg-transparent border-b py-3 text-stone-900 focus:outline-none transition-colors peer placeholder-transparent pr-8 ${isAdmin ? 'border-stone-300 focus:border-stone-800' : 'border-stone-300 focus:border-gold-600'}`,
                  placeholder: 'Password',
                  id: 'auth-password',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 444 },
                }),
                React.createElement(
                  'label',
                  {
                    htmlFor: 'auth-password',
                    className: `absolute left-0 -top-3.5 text-xs transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-xs uppercase tracking-wider ${isAdmin ? 'text-stone-800 peer-focus:text-stone-800' : 'text-gold-600 peer-focus:text-gold-600'}`,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 454 },
                  },
                  'Password'
                ),
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: () => setShowPassword(!showPassword),
                    className: 'absolute right-0 top-3 text-stone-400 hover:text-stone-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 457 },
                  },

                  showPassword
                    ? React.createElement(EyeOff, {
                        size: 18,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 462 },
                      })
                    : React.createElement(Eye, {
                        size: 18,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 462 },
                      })
                )
              ),

              !isLogin &&
                !isAdmin &&
                React.createElement(
                  'div',
                  {
                    className: 'group relative',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 467 },
                  },
                  React.createElement('input', {
                    type: showPassword ? 'text' : 'password',
                    name: 'confirmPassword',
                    value: formData.confirmPassword,
                    onChange: handleChange,
                    required: true,
                    className:
                      'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent',
                    placeholder: 'Confirm Password',
                    id: 'auth-confirm-password',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 468 },
                  }),
                  React.createElement(
                    'label',
                    {
                      htmlFor: 'auth-confirm-password',
                      className:
                        'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 478 },
                    },
                    'Confirm Password'
                  )
                ),

              isLogin &&
                React.createElement(
                  'div',
                  {
                    className: 'flex justify-end',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 485 },
                  },
                  React.createElement(
                    'button',
                    {
                      type: 'button',
                      onClick: () => setShowForgotPassword(true),
                      className: `text-xs hover:underline transition-colors ${isAdmin ? 'text-stone-500 hover:text-stone-800' : 'text-stone-400 hover:text-gold-600'}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 486 },
                    },
                    'Forgot Password?'
                  )
                ),

              React.createElement(
                'button',
                {
                  type: 'submit',
                  disabled: isLoading,
                  className: `w-full text-white py-4 px-8 uppercase tracking-[0.2em] text-xs transition-colors duration-300 flex items-center justify-center gap-2 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed group mt-8 ${isAdmin ? 'bg-stone-800 hover:bg-stone-700' : 'bg-stone-900 hover:bg-gold-600'}`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 496 },
                },

                isLoading
                  ? React.createElement(
                      'span',
                      {
                        className: 'animate-pulse',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 502 },
                      },
                      'Processing...'
                    )
                  : React.createElement(
                      React.Fragment,
                      null,
                      isAdmin ? 'Authenticate' : isLogin ? 'Sign In' : 'Create Account',
                      React.createElement(ArrowRight, {
                        size: 14,
                        className: 'group-hover:translate-x-1 transition-transform',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 506 },
                      })
                    )
              )
            ),

        /* Toggle Modes */
        React.createElement(
          'div',
          {
            className: 'mt-12 text-center border-t border-stone-100 pt-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 514 },
          },
          !isAdmin
            ? React.createElement(
                React.Fragment,
                null,
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-500 text-sm font-light',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 517 },
                  },
                  isLogin ? 'Not a member yet?' : 'Already have an account?'
                ),
                React.createElement(
                  'button',
                  {
                    onClick: toggleMode,
                    className:
                      'mt-2 text-stone-900 uppercase tracking-widest text-xs font-bold hover:text-gold-600 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 520 },
                  },

                  isLogin ? 'Create Account' : 'Sign In'
                )
              )
            : React.createElement(
                'button',
                {
                  onClick: () => onNavigate('auth'),
                  className:
                    'text-stone-500 uppercase tracking-widest text-[10px] font-bold hover:text-stone-900 transition-colors flex items-center justify-center gap-2 mx-auto',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 528 },
                },

                React.createElement(ArrowLeft, {
                  size: 12,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 532 },
                }),
                ' Return to Member Login'
              )
        )
      )
    ),

    /* Forgot Password Modal */
    showForgotPassword &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[200] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 541 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/70 backdrop-blur-sm',
          onClick: () => setShowForgotPassword(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 542 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-md rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 543 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 544 },
            },
            React.createElement(
              'h3',
              {
                className: 'font-serif text-xl',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 545 },
              },
              'Reset Password'
            ),
            React.createElement(
              'p',
              {
                className: 'text-xs text-stone-400 mt-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 546 },
              },
              'Enter your email to receive reset instructions'
            )
          ),

          React.createElement(
            'form',
            {
              onSubmit: handleForgotPassword,
              className: 'p-8 space-y-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 549 },
            },
            resetMessage &&
              React.createElement(
                'div',
                {
                  className: `p-3 rounded flex items-center gap-3 text-sm ${
                    resetSuccess ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 551 },
                },
                resetSuccess
                  ? React.createElement(Check, {
                      size: 16,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 554 },
                    })
                  : React.createElement(AlertCircle, {
                      size: 16,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 554 },
                    }),
                React.createElement(
                  'span',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 555 } },
                  resetMessage
                )
              ),

            React.createElement(
              'div',
              {
                className: 'group relative',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 559 },
              },
              React.createElement('input', {
                type: 'email',
                value: resetEmail,
                onChange: (e) => setResetEmail(e.target.value),
                required: true,
                className:
                  'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent',
                placeholder: 'Email Address',
                id: 'reset-email',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 560 },
              }),
              React.createElement(
                'label',
                {
                  htmlFor: 'reset-email',
                  className:
                    'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 569 },
                },
                'Email Address'
              )
            ),

            React.createElement(
              'div',
              {
                className: 'flex gap-4 pt-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 574 },
              },
              React.createElement(
                'button',
                {
                  type: 'button',
                  onClick: () => setShowForgotPassword(false),
                  className:
                    'flex-1 border border-stone-300 text-stone-600 py-3 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 575 },
                },
                'Cancel'
              ),
              React.createElement(
                'button',
                {
                  type: 'submit',
                  disabled: isResetting,
                  className:
                    'flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 582 },
                },

                isResetting
                  ? React.createElement(
                      React.Fragment,
                      null,
                      React.createElement(RefreshCw, {
                        size: 14,
                        className: 'animate-spin',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 589 },
                      }),
                      'Sending...'
                    )
                  : 'Send Reset Link'
              )
            )
          )
        )
      )
  );
};

export default Auth;
